import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Eye, FileDown, Loader2, Search, X, Plus, Trash2, Copy, Ban, BookmarkPlus,
  AlertTriangle, RotateCcw, ChevronDown, ChevronRight, Pencil, ExternalLink, Sparkles, Download, Send, RefreshCw, BellRing,
} from 'lucide-react';
import { api } from '@/lib/api';
import {
  CONDICIONES_VACIAS, ClavePlantilla, Clausula, ClausulaExtra, Contrato, DESCRIPCION_MODELO, DocumentoResuelto, ESTADOS_CS_REENVIABLES,
  ESTADO_CS, EstadoContrato, FORMATO_CLAUSULA, FirmanteContrato, FirmanteEntrada, FormatoClausula, ModeloContrato, NOMBRE_MODELO, PlantillaVersion,
  Preajuste, PreparacionFirma, ROL_FIRMANTE, RecordatoriosConfig, RecordatoriosFirma, RespuestaParte, Seccion, Sobrescritura, TIPO_DIFERENCIA, Tabla, TipoParte,
  VariableDef, Vivienda, calendarioRecordatorios, estadoRecordatorio, idNuevaClausula, inicioTemporada, lapsoLegible, temporadaEmpezada, valorBase, varianteAutomatica,
} from '@/lib/contratos';
import { classNames, formatDateTime } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import VistaPreviaContrato from '@/components/contratos/VistaPreviaContrato';
import HistorialLista from '@/components/contratos/HistorialLista';
import ConsultaCard from '@/components/contratos/ConsultaCard';
import { EstadoContratoBadge, enlaceParte } from './ContratosListaPage';

// ─── Utilidades ─────────────────────────────────────────────────────────────

/** Pinta el texto con las variables como fichas, para que se vea qué se rellena solo. */
function TextoConVariables({ texto }: { texto: string }) {
  const partes = texto.split(/(\{\{\s*[a-zA-Z_][a-zA-Z0-9_]*\s*\}\})/g);
  return (
    <>
      {partes.map((p, i) =>
        /^\{\{/.test(p)
          ? <span key={i} className="inline-block rounded bg-sereno-blue/60 text-sereno-dark px-1 font-mono text-[11px] align-baseline">{p.replace(/[{}\s]/g, '')}</span>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

function faltantes(info: RespuestaParte, modelo: ClavePlantilla, segundoTitular: boolean, propiedadIds: string[], enBlanco: number): string[] {
  // El nombre completo va SIEMPRE en blanco (lo escribe el titular según su
  // DNI, ver filasTitular en el servidor), así que encabeza la lista aunque la
  // ficha lo tenga.
  const lista: string[] = ['Nombre completo del titular'];
  const t = info.datos.titulares[0];
  if (t) {
    if (!t.dni) lista.push('DNI/NIE/NIF del titular');
    if (!t.domicilio) lista.push('Domicilio del titular');
    if (!t.iban) lista.push('IBAN');
    if (!t.email) lista.push('Correo electrónico');
  }
  if (segundoTitular) {
    const t2 = info.datos.titulares[1];
    lista.push('Nombre completo del segundo titular');
    if (!t2?.dni) lista.push('DNI del segundo titular');
  }
  const viviendas: Vivienda[] = info.datos.tipo === 'owner'
    ? info.viviendasDisponibles.filter(v => v.id && propiedadIds.includes(v.id))
    : info.datos.viviendas;
  viviendas.forEach((v, i) => {
    const n = v.nombre || `vivienda ${i + 1}`;
    if (!v.codigo) lista.push(`Código de inscripción de ${n}`);
    if (modelo === 'explotacion' && !v.catastro) lista.push(`Referencia catastral de ${n}`);
    if (modelo === 'gestion' && !v.nra) lista.push(`NRA de ${n}`);
  });
  if (enBlanco > 0) lista.push(`${enBlanco} vivienda${enBlanco > 1 ? 's' : ''} sin ficha: todos sus datos irán en blanco`);
  return lista;
}

function DatosQueFaltan({ lista }: { lista: string[] }) {
  return (
    <div className="text-xs text-amber-900 bg-amber-50 rounded-lg px-3 py-2">
      <p className="font-medium flex items-center gap-1 mb-1"><AlertTriangle size={12} /> Saldrán como huecos rellenables en Click &amp; Sign:</p>
      <ul className="list-disc pl-4 space-y-0.5">{lista.map((f, i) => <li key={i}>{f}</li>)}</ul>
    </div>
  );
}

// ─── Nuevo contrato ─────────────────────────────────────────────────────────

interface Resultado { tipo: TipoParte; id: string; nombre: string; detalle: string }

function NuevoContrato({ tipoInicial, idInicial }: { tipoInicial?: TipoParte; idInicial?: string }) {
  const navigate = useNavigate();
  const [parte, setParte] = useState<{ tipo: TipoParte; id: string } | null>(tipoInicial && idInicial ? { tipo: tipoInicial, id: idInicial } : null);
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState<Resultado[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [info, setInfo] = useState<RespuestaParte | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modelo, setModelo] = useState<ClavePlantilla>('explotacion');
  const [modeloFinalizado, setModeloFinalizado] = useState<ModeloContrato>('explotacion');
  const [preajustes, setPreajustes] = useState<Preajuste[]>([]);
  const [preajusteId, setPreajusteId] = useState('');
  const [propiedadIds, setPropiedadIds] = useState<string[]>([]);
  const [enBlanco, setEnBlanco] = useState(0);
  const [segundo, setSegundo] = useState(false);
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    if (parte) return;
    const texto = q.trim();
    if (texto.length < 2) { setResultados([]); return; }
    let vigente = true;
    setBuscando(true);
    const t = setTimeout(() => {
      Promise.all([
        api.getOwners({ search: texto }).catch(() => [] as any[]),
        api.getLeads({ search: texto }).catch(() => [] as any[]),
      ]).then(([owners, leads]) => {
        if (!vigente) return;
        const nombre = (x: any) => `${x.firstName || ''} ${x.lastName || ''}`.trim() || x.email || '(sin nombre)';
        setResultados([
          ...(owners as any[]).slice(0, 8).map(o => ({ tipo: 'owner' as TipoParte, id: o.id, nombre: nombre(o), detalle: [o.email, (o.propertyNames || []).join(', ')].filter(Boolean).join(' · ') })),
          ...(leads as any[]).filter(l => l.status !== 'converted' && l.type !== 'peticion').slice(0, 8).map(l => ({ tipo: 'lead' as TipoParte, id: l.id, nombre: nombre(l), detalle: [l.email, l.propertyAddress].filter(Boolean).join(' · ') })),
        ]);
      }).finally(() => { if (vigente) setBuscando(false); });
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  }, [q, parte]);

  useEffect(() => {
    if (!parte) { setInfo(null); return; }
    let vigente = true;
    setCargando(true);
    setError(null);
    api.getParteContrato(parte.tipo, parte.id).then(r => {
      if (!vigente) return;
      setInfo(r);
      setModelo(r.modeloSugerido);
      setModeloFinalizado(r.modeloSugerido);
      setSegundo(r.datos.titulares.length > 1);
      setPropiedadIds(r.viviendasDisponibles.filter(v => v.id).map(v => v.id as string));
      setEnBlanco(parte.tipo === 'lead' && r.datos.viviendas.length === 0 ? 1 : 0);
    }).catch(e => { if (vigente) setError(e.message); }).finally(() => { if (vigente) setCargando(false); });
    return () => { vigente = false; };
  }, [parte]);

  useEffect(() => {
    let vigente = true;
    api.getPreajustesContrato(modelo).then(p => { if (vigente) setPreajustes(p); }).catch(() => setPreajustes([]));
    setPreajusteId('');
    return () => { vigente = false; };
  }, [modelo]);

  const crear = async () => {
    if (!parte || !info) return;
    setCreando(true);
    try {
      const c = await api.crearContrato({
        templateKey: modelo,
        parte,
        propiedadIds: parte.tipo === 'owner' ? propiedadIds : [],
        viviendasEnBlanco: enBlanco,
        segundoTitular: segundo,
        preajusteId: preajusteId || null,
        valores: modelo === 'finalizacion' ? { modelo_finalizado: modeloFinalizado } : {},
      });
      navigate(`/contracts/c/${c.id}`, { replace: true });
    } catch (e: any) {
      alert(e.message || 'No se pudo crear el contrato');
    } finally {
      setCreando(false);
    }
  };

  const preajuste = preajustes.find(p => p.id === preajusteId) || null;
  const lista = info ? faltantes(info, modelo, segundo, propiedadIds, enBlanco) : [];

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/contracts" className="p-2 rounded-lg hover:bg-sereno-blue/30 text-sereno-dark/50"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-2xl font-bold">Nuevo contrato</h1>
          <p className="text-sm text-sereno-dark/50">Se crea como borrador: después eliges condiciones, revisas el texto y generas el PDF.</p>
        </div>
      </div>

      <section className="card">
        <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">1 · Para quién</h2>
        {!parte ? (
          <div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
              <input autoFocus className="input pl-9" placeholder="Busca un propietario o un lead por nombre, correo o teléfono…" value={q} onChange={e => setQ(e.target.value)} />
              {buscando && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-sereno-dark/40" />}
            </div>
            {resultados.length > 0 && (
              <ul className="mt-2 divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {resultados.map(r => (
                  <li key={`${r.tipo}-${r.id}`}>
                    <button onClick={() => setParte({ tipo: r.tipo, id: r.id })} className="w-full text-left px-3 py-2 hover:bg-sereno-blue/20 flex items-center gap-3">
                      <span className={classNames('badge', r.tipo === 'owner' ? 'bg-sereno-blue text-sereno-dark' : 'bg-amber-100 text-amber-800')}>{r.tipo === 'owner' ? 'Propietario' : 'Lead'}</span>
                      <span className="font-medium">{r.nombre}</span>
                      <span className="text-xs text-sereno-dark/40 truncate">{r.detalle}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {q.trim().length >= 2 && !buscando && resultados.length === 0 && <p className="text-sm text-sereno-dark/40 mt-2">Sin resultados.</p>}
          </div>
        ) : cargando ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-sm text-red-600 flex items-center gap-2">{error} <button onClick={() => setParte(null)} className="underline">Elegir otra</button></div>
        ) : info ? (
          <div className="flex items-start gap-3">
            <span className={classNames('badge mt-0.5', info.datos.tipo === 'owner' ? 'bg-sereno-blue text-sereno-dark' : 'bg-amber-100 text-amber-800')}>{info.datos.tipo === 'owner' ? 'Propietario' : 'Lead'}</span>
            <div className="flex-1">
              <p className="font-medium">{info.datos.nombre}</p>
              <p className="text-xs text-sereno-dark/50">{[info.datos.email, info.datos.telefono].filter(Boolean).join(' · ')}</p>
              {info.datos.tipo === 'lead' && (
                <p className="text-xs text-sereno-dark/50 mt-1">El contrato quedará colgado del lead; al convertirlo en propietario pasa a su ficha y el PDF a sus documentos.</p>
              )}
            </div>
            {!tipoInicial && <button onClick={() => setParte(null)} className="btn-ghost text-xs py-1">Cambiar</button>}
          </div>
        ) : null}
      </section>

      {info && (
        <>
          <section className="card">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">2 · Modelo y punto de partida</h2>
            <div className="grid gap-3 sm:grid-cols-3 mb-4">
              {(Object.keys(NOMBRE_MODELO) as ClavePlantilla[]).map(k => (
                <label key={k} className={classNames('flex items-start gap-3 rounded-lg border p-3 cursor-pointer', modelo === k ? 'border-sereno-dark bg-sereno-blue/20' : 'border-gray-200 hover:border-sereno-dark/40')}>
                  <input type="radio" name="modelo" className="mt-1" checked={modelo === k} onChange={() => setModelo(k)} />
                  <span>
                    <span className="font-medium block">{NOMBRE_MODELO[k]}</span>
                    <span className="text-xs text-sereno-dark/50">{DESCRIPCION_MODELO[k]}</span>
                    {info.modeloSugerido === k && <span className="badge bg-emerald-100 text-emerald-800 ml-2">según la ficha</span>}
                  </span>
                </label>
              ))}
            </div>
            {modelo === 'finalizacion' && (
              <div className="mb-4">
                <label className="label">Contrato que se finaliza</label>
                <div className="flex flex-wrap gap-4">
                  {(['gestion', 'explotacion'] as ModeloContrato[]).map(k => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" name="modelo-finalizado" checked={modeloFinalizado === k} onChange={() => setModeloFinalizado(k)} />
                      {NOMBRE_MODELO[k]}
                      {info.modeloSugerido === k && <span className="badge bg-emerald-100 text-emerald-800">según la ficha</span>}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-sereno-dark/50 mt-1">Las cláusulas que dependen del modelo (liquidación final, registros, datos de huéspedes…) salen con la redacción de ese contrato.</p>
              </div>
            )}
            <label className="label">Preajuste (condiciones ya negociadas)</label>
            <select className="input" value={preajusteId} onChange={e => setPreajusteId(e.target.value)}>
              <option value="">Estándar: la plantilla tal cual</option>
              {preajustes.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
            {preajuste?.descripcion && <p className="text-xs text-sereno-dark/50 mt-1">{preajuste.descripcion}</p>}
          </section>

          <section className="card">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">3 · Viviendas y titulares</h2>
            {info.datos.tipo === 'owner' ? (
              info.viviendasDisponibles.length === 0 ? (
                <p className="text-sm text-sereno-dark/50 mb-3">El propietario no tiene propiedades en el CRM. Indica cuántas viviendas van en blanco.</p>
              ) : (
                <ul className="space-y-1.5 mb-3">
                  {info.viviendasDisponibles.map(v => (
                    <li key={v.id}>
                      <label className="flex items-start gap-3 text-sm cursor-pointer">
                        <input type="checkbox" className="mt-1" checked={!!v.id && propiedadIds.includes(v.id)} onChange={e => setPropiedadIds(ids => e.target.checked ? [...ids, v.id as string] : ids.filter(x => x !== v.id))} />
                        <span>
                          <span className="font-medium">{v.nombre || v.direccion}</span>
                          <span className="text-xs text-sereno-dark/50 block">{[v.direccion, v.codigo ? `Licencia ${v.codigo}` : 'sin licencia en la ficha', modelo === 'explotacion' ? (v.catastro ? 'ref. catastral ✓' : 'sin ref. catastral') : modelo === 'gestion' ? (v.nra ? 'NRA ✓' : 'sin NRA') : null].filter(Boolean).join(' · ')}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              )
            ) : info.datos.viviendas.length > 0 ? (
              <p className="text-sm mb-3">Vivienda del lead: <span className="font-medium">{info.datos.viviendas[0].direccion}</span> <span className="text-xs text-sereno-dark/50">(licencia y referencias en blanco)</span></p>
            ) : (
              <p className="text-sm text-sereno-dark/50 mb-3">El lead no tiene dirección de vivienda: irá en blanco.</p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="label">Viviendas adicionales en blanco</label>
                <input type="number" min={0} max={10} className="input" value={enBlanco} onChange={e => setEnBlanco(Math.max(0, Math.min(10, Number(e.target.value) || 0)))} />
              </div>
              <div>
                <label className="label">Titulares</label>
                <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
                  <input type="checkbox" checked={segundo} onChange={e => setSegundo(e.target.checked)} />
                  Dos titulares (doble identificación y doble firma)
                </label>
                {info.datos.titulares[1]?.nombre && <p className="text-xs text-sereno-dark/50 mt-1">Segundo titular en la ficha: {info.datos.titulares[1].nombre}</p>}
              </div>
            </div>
            <div className="mt-4"><DatosQueFaltan lista={lista} /></div>
          </section>

          <div className="flex items-center justify-end gap-2">
            <Link to="/contracts" className="btn-ghost text-sm">Cancelar</Link>
            <button onClick={crear} disabled={creando} className="btn-primary text-sm flex items-center gap-1.5">
              {creando ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Crear borrador y seguir
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Editor del borrador ────────────────────────────────────────────────────

interface Form {
  preajusteId: string | null;
  propiedadIds: string[];
  viviendasEnBlanco: number;
  segundoTitular: boolean;
  valores: Record<string, string | number>;
  variantes: Record<string, string>;
  sobrescrituras: Record<string, Sobrescritura>;
  clausulasExtra: ClausulaExtra[];
  notas: string;
}

function desdeContrato(c: Contrato): Form {
  return {
    preajusteId: c.preajusteId || null,
    propiedadIds: c.propiedadIds || [],
    viviendasEnBlanco: c.viviendasEnBlanco || 0,
    segundoTitular: !!c.segundoTitular,
    valores: c.valores || {},
    variantes: c.variantes || {},
    sobrescrituras: c.sobrescrituras || {},
    clausulasExtra: c.clausulasExtra || [],
    notas: c.notas || '',
  };
}

/** Cláusulas de una sección con las extra ya intercaladas, como hace el servidor. */
function conExtras(s: Seccion, extras: ClausulaExtra[]): { clausula: Clausula; extra: ClausulaExtra | null }[] {
  const lista: { clausula: Clausula; extra: ClausulaExtra | null }[] = s.clausulas.map(c => ({ clausula: c, extra: null }));
  const alFinal: ClausulaExtra[] = [];
  for (const e of extras.filter(x => x.seccionId === s.id)) {
    const pos = e.despuesDe ? lista.findIndex(x => x.clausula.id === e.despuesDe) : -1;
    if (pos === -1) alFinal.push(e);
    else lista.splice(pos + 1, 0, { clausula: e.clausula, extra: e });
  }
  for (const e of alFinal) lista.push({ clausula: e.clausula, extra: e });
  return lista;
}

function tablaATexto(t?: Tabla): string {
  return t ? t.filas.map(f => f.join(' | ')).join('\n') : '';
}

function textoATabla(cabecera: string[] | null, cuerpo: string): Tabla {
  return { cabecera, filas: cuerpo.split('\n').map(l => l.trim()).filter(Boolean).map(l => l.split('|').map(c => c.trim())) };
}

interface FilaProps {
  clausula: Clausula;
  extra: ClausulaExtra | null;
  form: Form;
  preajuste: Preajuste | null;
  /** Valores efectivos de las variables, para las variantes que se aplican solas. */
  valores: Record<string, string | number | undefined>;
  onForm: (f: (prev: Form) => Form) => void;
}

function FilaClausula({ clausula: c, extra, form, preajuste, valores, onForm }: FilaProps) {
  const [editando, setEditando] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const [cuerpoTabla, setCuerpoTabla] = useState('');

  const sobre = form.sobrescrituras[c.id];
  const sobrePreajuste = preajuste?.sobrescrituras?.[c.id];
  const varianteContrato = form.variantes[c.id];
  const variantePreajuste = preajuste?.variantes?.[c.id];
  const automatica = varianteAutomatica(c, valores);
  const varianteActiva = varianteContrato || variantePreajuste || automatica?.clave || 'estandar';
  // Las que se aplican solas no se eligen: dependen de una variable de Condiciones.
  const elegibles = (c.variantes || []).filter(v => !v.cuando);
  const eliminada = !!sobre?.eliminada || (!sobre && !!sobrePreajuste?.eliminada);

  // Lo que se verá: variante → sobrescritura del preajuste → sobrescritura del contrato.
  const variante = (c.variantes || []).find(v => v.clave === varianteActiva);
  const efectivaTitulo = sobre?.titulo ?? sobrePreajuste?.titulo ?? (variante?.titulo !== undefined ? variante.titulo : c.titulo);
  const efectivaTexto = sobre?.texto ?? sobrePreajuste?.texto ?? variante?.texto ?? c.texto ?? '';
  const efectivaTabla = sobre?.tabla ?? sobrePreajuste?.tabla ?? c.tabla;

  const abrirEditor = () => {
    setTitulo(efectivaTitulo || '');
    setTexto(efectivaTexto);
    setCuerpoTabla(tablaATexto(efectivaTabla));
    setEditando(true);
  };

  const guardarEdicion = () => {
    if (extra) {
      onForm(f => ({
        ...f,
        clausulasExtra: f.clausulasExtra.map(e => e.id === extra.id
          ? { ...e, clausula: { ...e.clausula, titulo: titulo || undefined, texto: c.formato === 'tabla' ? undefined : texto, tabla: c.formato === 'tabla' ? textoATabla(c.tabla?.cabecera ?? null, cuerpoTabla) : undefined } }
          : e),
      }));
    } else {
      const nueva: Sobrescritura = c.formato === 'tabla'
        ? { tabla: textoATabla(c.tabla?.cabecera ?? null, cuerpoTabla) }
        : { titulo: titulo || undefined, texto };
      onForm(f => ({ ...f, sobrescrituras: { ...f.sobrescrituras, [c.id]: { ...(f.sobrescrituras[c.id] || {}), ...nueva, eliminada: false } } }));
    }
    setEditando(false);
  };

  const restaurar = () => onForm(f => {
    const s = { ...f.sobrescrituras };
    delete s[c.id];
    return { ...f, sobrescrituras: s };
  });

  const quitar = () => onForm(f => ({ ...f, sobrescrituras: { ...f.sobrescrituras, [c.id]: { eliminada: true } } }));

  const quitarExtra = () => {
    if (!extra || !confirm('¿Quitar esta cláusula añadida?')) return;
    onForm(f => ({ ...f, clausulasExtra: f.clausulasExtra.filter(e => e.id !== extra.id) }));
  };

  const elegirVariante = (clave: string) => onForm(f => {
    const v = { ...f.variantes };
    if (clave === (variantePreajuste || automatica?.clave || 'estandar')) delete v[c.id];
    else v[c.id] = clave;
    return { ...f, variantes: v };
  });

  if (c.formato === 'especial') {
    const nombre = c.especial === 'identificacion-titular' ? 'Identificación del titular' : c.especial === 'identificacion-viviendas' ? 'Identificación de las viviendas' : 'Firmas, lugar y fecha';
    return (
      <div className="rounded-lg border border-dashed border-sereno-dark/20 bg-sereno-blue/10 px-3 py-2 text-xs text-sereno-dark/60 flex items-center gap-2">
        <span className="badge bg-sereno-blue text-sereno-dark">Datos de la ficha</span> {nombre}: se rellena solo; lo que falte sale como hueco.
      </div>
    );
  }

  const modificada = !!(sobre && !sobre.eliminada && (sobre.texto !== undefined || sobre.titulo !== undefined || sobre.tabla !== undefined));
  const modificadaPreajuste = !modificada && !!(sobrePreajuste && !sobrePreajuste.eliminada);

  return (
    <div className={classNames('rounded-lg border px-3 py-2', eliminada ? 'border-gray-200 bg-gray-50 opacity-70' : modificada || (varianteContrato && varianteContrato !== 'estandar') ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-white')}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span className="text-[10px] uppercase tracking-wider text-sereno-dark/40">{FORMATO_CLAUSULA[c.formato]}{c.nivel === 1 ? ' · sangrada' : ''}</span>
            {extra && <span className="badge bg-emerald-100 text-emerald-800">Añadida</span>}
            {eliminada && <span className="badge bg-gray-200 text-gray-600">Quitada</span>}
            {modificada && <span className="badge bg-amber-100 text-amber-800">Modificada</span>}
            {modificadaPreajuste && <span className="badge bg-amber-100/60 text-amber-800">Modificada por el preajuste</span>}
            {varianteActiva !== 'estandar' && variante && (
              variante.cuando && !varianteContrato && !variantePreajuste
                ? <span className="badge bg-gray-100 text-gray-600" title="Cambia sola según las condiciones del contrato">Redacción de {variante.nombre.toLowerCase()}</span>
                : <span className="badge bg-sereno-blue text-sereno-dark">Variante: {variante.nombre}{!varianteContrato && variantePreajuste ? ' (preajuste)' : ''}</span>
            )}
          </div>
          {!editando && (
            c.formato === 'tabla' ? (
              <p className="text-xs text-sereno-dark/60">Tabla · {(efectivaTabla?.filas || []).length} filas · {(efectivaTabla?.filas || []).map(f => f.join(' / ')).join(' ; ')}</p>
            ) : (
              <p className={classNames('text-sm leading-relaxed', eliminada && 'line-through')}>
                {efectivaTitulo && <strong>{efectivaTitulo}{efectivaTexto ? ': ' : ''}</strong>}<TextoConVariables texto={efectivaTexto} />
              </p>
            )
          )}
        </div>
        {!editando && !eliminada && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={abrirEditor} className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Editar el texto solo para este contrato"><Pencil size={14} /></button>
            {extra ? (
              <button onClick={quitarExtra} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600" title="Quitar cláusula añadida"><Trash2 size={14} /></button>
            ) : (
              <>
                {(sobre || varianteContrato) && <button onClick={restaurar} className="p-1.5 rounded hover:bg-sereno-blue/30 text-sereno-dark/40 hover:text-sereno-dark" title="Volver al texto de la plantilla"><RotateCcw size={14} /></button>}
                <button onClick={quitar} className="p-1.5 rounded hover:bg-red-50 text-sereno-dark/40 hover:text-red-600" title="Quitar esta cláusula del contrato"><X size={14} /></button>
              </>
            )}
          </div>
        )}
        {eliminada && !extra && <button onClick={restaurar} className="btn-ghost text-xs py-1 flex items-center gap-1"><RotateCcw size={12} /> Restaurar</button>}
      </div>

      {!editando && !eliminada && elegibles.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {[{ clave: 'estandar', nombre: 'Redacción estándar' }, ...elegibles].map(v => (
            <label key={v.clave} className={classNames('flex items-center gap-1.5 text-xs rounded-full border px-2.5 py-1 cursor-pointer', varianteActiva === v.clave ? 'border-sereno-dark bg-sereno-dark text-white' : 'border-gray-200 hover:border-sereno-dark/40')}>
              <input type="radio" className="hidden" name={`var-${c.id}`} checked={varianteActiva === v.clave} onChange={() => elegirVariante(v.clave)} />
              {v.nombre}
            </label>
          ))}
        </div>
      )}

      {editando && (
        <div className="mt-2 space-y-2">
          {c.formato === 'tabla' ? (
            <textarea className="input font-mono text-sm" rows={6} value={cuerpoTabla} onChange={e => setCuerpoTabla(e.target.value)} placeholder="Una fila por línea, celdas separadas por |" />
          ) : (
            <>
              {(c.formato === 'parrafo' || c.formato === 'vineta' || c.formato === 'numerada' || c.formato === 'subtitulo') && (
                <input className="input text-sm" placeholder="Título en negrita (opcional)" value={titulo} onChange={e => setTitulo(e.target.value)} />
              )}
              {c.formato !== 'subtitulo' && (
                <textarea className="input text-sm leading-relaxed" rows={Math.min(14, Math.max(3, Math.ceil(texto.length / 100)))} value={texto} onChange={e => setTexto(e.target.value)} />
              )}
            </>
          )}
          <div className="flex items-center gap-2">
            <button onClick={guardarEdicion} className="btn-primary text-xs py-1">Aplicar a este contrato</button>
            <button onClick={() => setEditando(false)} className="btn-ghost text-xs py-1">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function NuevaClausulaExtra({ seccion, ultimaId, onAdd }: { seccion: Seccion; ultimaId: string | null; onAdd: (e: ClausulaExtra) => void }) {
  const [abierto, setAbierto] = useState(false);
  const [formato, setFormato] = useState<FormatoClausula>('parrafo');
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  if (!abierto) return <button onClick={() => setAbierto(true)} className="btn-ghost text-xs py-1 flex items-center gap-1"><Plus size={12} /> Añadir cláusula a «{seccion.titulo || 'Preámbulo'}»</button>;
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
      <div className="grid gap-2 sm:grid-cols-[150px_1fr]">
        <select className="input text-sm" value={formato} onChange={e => setFormato(e.target.value as FormatoClausula)}>
          {(['parrafo', 'vineta', 'numerada', 'subtitulo'] as FormatoClausula[]).map(f => <option key={f} value={f}>{FORMATO_CLAUSULA[f]}</option>)}
        </select>
        <input className="input text-sm" placeholder="Título en negrita (opcional)" value={titulo} onChange={e => setTitulo(e.target.value)} />
      </div>
      {formato !== 'subtitulo' && <textarea className="input text-sm" rows={4} placeholder="Texto de la cláusula" value={texto} onChange={e => setTexto(e.target.value)} />}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            if (!titulo.trim() && !texto.trim()) return;
            const id = idNuevaClausula(titulo || texto);
            onAdd({ id, seccionId: seccion.id, despuesDe: ultimaId, clausula: { id, formato, nivel: 0, titulo: titulo.trim() || undefined, texto: formato === 'subtitulo' ? undefined : texto.trim() } });
            setTitulo(''); setTexto(''); setAbierto(false);
          }}
          className="btn-primary text-xs py-1"
        >
          Añadir
        </button>
        <button onClick={() => setAbierto(false)} className="btn-ghost text-xs py-1">Cancelar</button>
      </div>
    </div>
  );
}

function EditorBorrador({ contrato, onCambio }: { contrato: Contrato; onCambio: (c: Contrato) => void }) {
  const navigate = useNavigate();
  const [plantilla, setPlantilla] = useState<PlantillaVersion | null>(null);
  const [info, setInfo] = useState<RespuestaParte | null>(null);
  const [preajustes, setPreajustes] = useState<Preajuste[]>([]);
  const [form, setForm] = useState<Form>(() => desdeContrato(contrato));
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [previa, setPrevia] = useState<DocumentoResuelto | null>(null);
  const [cargandoPrevia, setCargandoPrevia] = useState(false);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  // Hasta que se toca el acordeón, las secciones con cambios salen abiertas solas.
  const [autoAbrir, setAutoAbrir] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setError(null);
    Promise.all([
      api.getPlantillaContrato(contrato.templateKey, contrato.templateVersion),
      api.getParteContrato(contrato.parte.tipo, contrato.parte.id),
      api.getPreajustesContrato(contrato.templateKey),
    ]).then(([p, i, pre]) => {
      if (!vigente) return;
      setPlantilla(p); setInfo(i); setPreajustes(pre);
    }).catch(e => { if (vigente) setError(e.message); });
    return () => { vigente = false; };
  }, [contrato.templateKey, contrato.templateVersion, contrato.parte.tipo, contrato.parte.id]);

  const onForm = useCallback((fn: (prev: Form) => Form) => { setForm(fn); setSucio(true); }, []);
  const preajuste = preajustes.find(p => p.id === form.preajusteId) || null;

  const guardar = useCallback(async (): Promise<Contrato> => {
    setGuardando(true);
    try {
      const c = await api.actualizarContrato(contrato.id, { ...form, notas: form.notas || undefined });
      onCambio(c);
      setSucio(false);
      return c;
    } finally {
      setGuardando(false);
    }
  }, [contrato.id, form, onCambio]);

  const verPrevia = async () => {
    setCargandoPrevia(true);
    try {
      if (sucio) await guardar();
      setPrevia(await api.vistaPreviaContrato(contrato.id));
    } catch (e: any) {
      alert(e.message || 'No se pudo generar la vista previa');
    } finally {
      setCargandoPrevia(false);
    }
  };

  const generar = async () => {
    if (!confirm(`¿Generar el PDF del contrato ${contrato.codigo}? El texto queda congelado; para cambiarlo después habrá que duplicarlo.`)) return;
    setGenerando(true);
    try {
      if (sucio) await guardar();
      onCambio(await api.generarContrato(contrato.id));
    } catch (e: any) {
      alert(e.message || 'No se pudo generar el PDF');
    } finally {
      setGenerando(false);
    }
  };

  const guardarPreajuste = async () => {
    const nombre = prompt('Nombre del preajuste (p. ej. «79 % con dos quincenas»):');
    if (!nombre?.trim()) return;
    try {
      if (sucio) await guardar();
      await api.guardarPreajusteDesdeContrato(contrato.id, nombre.trim());
      setPreajustes(await api.getPreajustesContrato(contrato.templateKey));
      alert('Preajuste guardado. Lo verás al crear el siguiente contrato.');
    } catch (e: any) {
      alert(e.message || 'No se pudo guardar el preajuste');
    }
  };

  const eliminar = async () => {
    if (!confirm(`¿Eliminar el borrador ${contrato.codigo}?`)) return;
    try {
      await api.eliminarContrato(contrato.id);
      navigate('/contracts');
    } catch (e: any) {
      alert(e.message || 'No se pudo eliminar');
    }
  };

  const cambiarModelo = async (key: ClavePlantilla) => {
    if (key === contrato.templateKey) return;
    if (!confirm('Cambiar el modelo descarta el preajuste, las variantes y los retoques elegidos. ¿Seguir?')) return;
    try {
      const c = await api.actualizarContrato(contrato.id, { templateKey: key, preajusteId: null, variantes: {}, sobrescrituras: {}, clausulasExtra: [] });
      onCambio(c);
      setForm(desdeContrato(c));
      setSucio(false);
    } catch (e: any) {
      alert(e.message || 'No se pudo cambiar el modelo');
    }
  };

  const variantesActivas = useMemo(() => new Set(Object.values({ ...(preajuste?.variantes || {}), ...form.variantes })), [preajuste, form.variantes]);
  const variablesVisibles = (plantilla?.variables || []).filter(v => !v.calculada && (!v.soloVariante || variantesActivas.has(v.soloVariante)));
  const calculadas = (plantilla?.variables || []).filter(v => v.calculada);
  const valoresEfectivos = useMemo(() => {
    const salida: Record<string, string | number | undefined> = {};
    for (const v of plantilla?.variables || []) salida[v.clave] = form.valores[v.clave] ?? valorBase(v, preajuste, info?.modeloSugerido ?? null, contrato.generadoAt);
    return salida;
  }, [plantilla, form.valores, preajuste, info, contrato.generadoAt]);

  const nCambios = useMemo(() => {
    const variantes = Object.values(form.variantes).filter(v => v && v !== 'estandar').length;
    const sob = Object.values(form.sobrescrituras);
    return { variantes, modificadas: sob.filter(s => !s.eliminada).length, quitadas: sob.filter(s => s.eliminada).length, extras: form.clausulasExtra.length };
  }, [form]);

  const listaFaltantes = info ? faltantes(info, contrato.templateKey, form.segundoTitular, form.propiedadIds, form.viviendasEnBlanco) : [];

  if (error) return <div className="card border border-red-200 bg-red-50 text-red-700">{error}</div>;
  if (!plantilla || !info) return <LoadingSpinner />;

  const seccionTieneCambios = (s: Seccion) => s.clausulas.some(c => form.sobrescrituras[c.id] || (form.variantes[c.id] && form.variantes[c.id] !== 'estandar')) || form.clausulasExtra.some(e => e.seccionId === s.id);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="space-y-4 min-w-0">
        <section className="card">
          <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Contrato</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label">Modelo</label>
              <select className="input" value={contrato.templateKey} onChange={e => cambiarModelo(e.target.value as ClavePlantilla)}>
                {(Object.keys(NOMBRE_MODELO) as ClavePlantilla[]).map(k => <option key={k} value={k}>{NOMBRE_MODELO[k]}</option>)}
              </select>
              <p className="text-xs text-sereno-dark/40 mt-1">Plantilla v{contrato.templateVersion}</p>
            </div>
            <div>
              <label className="label">Preajuste</label>
              <select className="input" value={form.preajusteId || ''} onChange={e => onForm(f => ({ ...f, preajusteId: e.target.value || null }))}>
                <option value="">Estándar: la plantilla tal cual</option>
                {preajustes.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
              {preajuste?.descripcion && <p className="text-xs text-sereno-dark/40 mt-1">{preajuste.descripcion}</p>}
              {preajuste?.revisar && (
                <div className="mt-2 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-900">
                  <p className="font-medium flex items-center gap-1"><AlertTriangle size={12} /> Este preajuste está pendiente de revisar: la plantilla pasó de la v{preajuste.revisar.desdeVersion} a la v{preajuste.revisar.hastaVersion}.</p>
                  <ul className="list-disc pl-4 mt-1 space-y-0.5">{preajuste.revisar.motivos.map((m, i) => <li key={i}>{m}</li>)}</ul>
                </div>
              )}
            </div>
            <div>
              <label className="label">Viviendas incluidas</label>
              {info.datos.tipo === 'owner' && info.viviendasDisponibles.length > 0 ? (
                <ul className="space-y-1">
                  {info.viviendasDisponibles.map(v => (
                    <li key={v.id}>
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={!!v.id && form.propiedadIds.includes(v.id)} onChange={e => onForm(f => ({ ...f, propiedadIds: e.target.checked ? [...f.propiedadIds, v.id as string] : f.propiedadIds.filter(x => x !== v.id) }))} />
                        <span className="truncate">{v.nombre || v.direccion}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-sereno-dark/50">{info.datos.viviendas[0]?.direccion || 'Sin vivienda en la ficha'}</p>
              )}
              <div className="flex items-center gap-2 mt-2 text-sm">
                <span className="text-sereno-dark/60">+ en blanco</span>
                <input type="number" min={0} max={10} className="input w-20 py-1" value={form.viviendasEnBlanco} onChange={e => onForm(f => ({ ...f, viviendasEnBlanco: Math.max(0, Math.min(10, Number(e.target.value) || 0)) }))} />
              </div>
            </div>
            <div>
              <label className="label">Titulares</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input type="checkbox" checked={form.segundoTitular} onChange={e => onForm(f => ({ ...f, segundoTitular: e.target.checked }))} />
                Dos titulares
              </label>
              <label className="label mt-3">Notas internas</label>
              <input className="input" placeholder="Solo se ven en el CRM" value={form.notas} onChange={e => onForm(f => ({ ...f, notas: e.target.value }))} />
            </div>
          </div>
        </section>

        <section className="card">
          <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-3">Condiciones</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {variablesVisibles.map((v: VariableDef) => {
              const base = valorBase(v, preajuste, info.modeloSugerido, contrato.generadoAt) ?? '';
              const valor = form.valores[v.clave];
              const etiquetaOpcion = (x: string | number) => v.opciones?.find(o => o.valor === String(x))?.etiqueta ?? String(x);
              const origen = preajuste?.valores?.[v.clave] !== undefined ? 'preajuste' : v.desdeFicha ? 'según la ficha' : v.desdeFecha ? 'según la fecha' : 'estándar';
              return (
                <div key={v.clave}>
                  <label className="label">{v.etiqueta}</label>
                  <div className="flex items-center gap-2">
                    {v.tipo === 'opcion' ? (
                      <select
                        className="input"
                        value={valor ?? ''}
                        onChange={e => onForm(f => {
                          const val = { ...f.valores };
                          if (e.target.value === '') delete val[v.clave];
                          else val[v.clave] = e.target.value;
                          return { ...f, valores: val };
                        })}
                      >
                        <option value="">{base !== '' ? `${etiquetaOpcion(base)} (${origen})` : '—'}</option>
                        {(v.opciones || []).map(o => <option key={o.valor} value={o.valor}>{o.etiqueta}</option>)}
                      </select>
                    ) : (
                      <input
                        type={v.tipo === 'texto' ? 'text' : 'number'}
                        step={v.tipo === 'texto' ? undefined : '0.01'}
                        className="input"
                        placeholder={String(base)}
                        value={valor ?? ''}
                        onChange={e => onForm(f => {
                          const val = { ...f.valores };
                          if (e.target.value === '') delete val[v.clave];
                          else val[v.clave] = v.tipo === 'texto' ? e.target.value : Number(e.target.value);
                          return { ...f, valores: val };
                        })}
                      />
                    )}
                    {v.tipo === 'porcentaje' && <span className="text-sereno-dark/50">%</span>}
                  </div>
                  <p className="text-xs text-sereno-dark/40 mt-1">
                    {[
                      valor !== undefined && valor !== '' ? 'Cambiado para este contrato' : null,
                      // En una opción el valor de partida ya se lee en el desplegable.
                      v.tipo === 'opcion' ? null : preajuste?.valores?.[v.clave] !== undefined ? `Preajuste: ${preajuste.valores[v.clave]}` : v.desdeFecha ? `Según la fecha: ${base}` : `Estándar: ${v.porDefecto ?? '—'}`,
                      v.ayuda || null,
                    ].filter(Boolean).join(' · ')}
                  </p>
                </div>
              );
            })}
            {calculadas.map(v => (
              <div key={v.clave} className="text-sm text-sereno-dark/60 sm:col-span-2">
                <span className="font-medium">{v.etiqueta}</span>: se calcula como <code className="font-mono text-xs">{v.calculada}</code>{v.ayuda ? ` · ${v.ayuda}` : ''}
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider">Cláusulas</h2>
            <div className="flex items-center gap-2 text-xs">
              <button onClick={() => { setAutoAbrir(false); setAbiertas(new Set(plantilla.secciones.map(s => s.id))); }} className="btn-ghost py-1 px-2">Abrir todas</button>
              <button onClick={() => { setAutoAbrir(false); setAbiertas(new Set()); }} className="btn-ghost py-1 px-2">Cerrar todas</button>
            </div>
          </div>
          <p className="text-xs text-sereno-dark/50">Elige la variante donde la haya, edita el texto solo para este contrato o quita una cláusula. Todo lo que difiera de la plantilla aparece en la revisión.</p>
          {plantilla.secciones.map(s => {
            const abierta = abiertas.has(s.id) || (autoAbrir && seccionTieneCambios(s));
            const filas = conExtras(s, form.clausulasExtra);
            const alternar = () => {
              // Al primer clic se congela el estado automático para que cerrar una sección no reabra otras.
              setAbiertas(prev => {
                const n = autoAbrir ? new Set(plantilla.secciones.filter(seccionTieneCambios).map(x => x.id)) : new Set(prev);
                if (abierta) n.delete(s.id); else n.add(s.id);
                return n;
              });
              setAutoAbrir(false);
            };
            return (
              <div key={s.id} className="card p-0 overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-sereno-blue/10" onClick={alternar}>
                  {abierta ? <ChevronDown size={16} className="text-sereno-dark/40" /> : <ChevronRight size={16} className="text-sereno-dark/40" />}
                  <span className={classNames('badge', s.nivel === 1 ? 'bg-sereno-dark text-white' : 'bg-gray-100 text-gray-600')}>{s.nivel === 1 ? 'Anexo' : 'Apartado'}</span>
                  <span className="font-medium flex-1">{s.titulo || 'Preámbulo'}</span>
                  {seccionTieneCambios(s) && <span className="badge bg-amber-100 text-amber-800">con cambios</span>}
                  <span className="text-xs text-sereno-dark/40">{filas.length}</span>
                </div>
                {abierta && (
                  <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                    {filas.map(({ clausula, extra }) => (
                      <FilaClausula key={clausula.id} clausula={clausula} extra={extra} form={form} preajuste={preajuste} valores={valoresEfectivos} onForm={onForm} />
                    ))}
                    <NuevaClausulaExtra seccion={s} ultimaId={filas.length ? filas[filas.length - 1].clausula.id : null} onAdd={e => onForm(f => ({ ...f, clausulasExtra: [...f.clausulasExtra, e] }))} />
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>

      <aside className="space-y-4 lg:sticky lg:top-4 self-start">
        <div className="card space-y-3">
          <div>
            <p className="font-mono text-sm font-semibold">{contrato.codigo}</p>
            <EstadoContratoBadge estado={contrato.estado} />
          </div>
          <div className="text-sm">
            <Link to={enlaceParte(contrato)} className="font-medium hover:underline flex items-center gap-1">{contrato.parte.nombre} <ExternalLink size={12} /></Link>
            <p className="text-xs text-sereno-dark/50">{contrato.parte.tipo === 'owner' || contrato.ownerId ? 'Propietario' : 'Lead'} · {NOMBRE_MODELO[contrato.templateKey]} v{contrato.templateVersion}</p>
            <p className="text-xs text-sereno-dark/50">{preajuste ? `Preajuste: ${preajuste.nombre}` : 'Sin preajuste'}</p>
          </div>
          <div className="text-xs text-sereno-dark/60 grid grid-cols-2 gap-1">
            <span>Variantes: <b>{nCambios.variantes}</b></span>
            <span>Modificadas: <b>{nCambios.modificadas}</b></span>
            <span>Quitadas: <b>{nCambios.quitadas}</b></span>
            <span>Añadidas: <b>{nCambios.extras}</b></span>
          </div>
          <DatosQueFaltan lista={listaFaltantes} />
          <div className="grid gap-2 pt-2 border-t border-gray-100">
            <button onClick={() => guardar().catch(e => alert(e.message))} disabled={!sucio || guardando} className="btn-secondary text-sm flex items-center justify-center gap-1.5">
              {guardando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {sucio ? 'Guardar cambios' : 'Guardado'}
            </button>
            <button onClick={verPrevia} disabled={cargandoPrevia} className="btn-secondary text-sm flex items-center justify-center gap-1.5">
              {cargandoPrevia ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />} Revisar el texto
            </button>
            <button onClick={generar} disabled={generando} className="btn-primary text-sm flex items-center justify-center gap-1.5">
              {generando ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Generar PDF
            </button>
            <button onClick={guardarPreajuste} className="btn-ghost text-sm flex items-center justify-center gap-1.5"><BookmarkPlus size={14} /> Guardar como preajuste</button>
            <button onClick={eliminar} className="text-xs text-red-500 hover:text-red-700 flex items-center justify-center gap-1 py-1"><Trash2 size={12} /> Eliminar borrador</button>
          </div>
        </div>
        <ConsultaCard contrato={contrato} onCambio={onCambio} cambiosSinGuardar={sucio} />
        <div className="card">
          <p className="text-sm font-semibold mb-2">Historial</p>
          <HistorialLista entradas={contrato.historial} />
        </div>
      </aside>

      <Modal isOpen={!!previa} onClose={() => setPrevia(null)} title={`Revisión · ${contrato.codigo}`} size="2xl">
        {previa && (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg bg-sereno-blue/20 p-3 text-sm">
                <p className="font-semibold mb-1">Lo que difiere de la plantilla ({previa.diferencias.length})</p>
                {previa.diferencias.length === 0 ? <p className="text-sereno-dark/60">Nada: el contrato es la plantilla tal cual.</p> : (
                  <ul className="space-y-0.5 text-xs">
                    {previa.diferencias.map((d, i) => (
                      <li key={i}><span className="badge bg-white text-sereno-dark mr-1">{TIPO_DIFERENCIA[d.tipo]}</span>{d.titulo || d.clausulaId}{d.detalle ? ` → ${d.detalle}` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg bg-gray-50 p-3 text-sm">
                <p className="font-semibold mb-1">Variables</p>
                <ul className="text-xs font-mono space-y-0.5">
                  {Object.entries(previa.variables).map(([k, v]) => <li key={k}>{k} = {v}</li>)}
                </ul>
                {previa.variablesSinValor.length > 0 && (
                  <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle size={12} /> Sin valor: {previa.variablesSinValor.join(', ')}</p>
                )}
              </div>
            </div>
            <VistaPreviaContrato documento={previa} />
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Firma con Click & Sign ─────────────────────────────────────────────────

function EstadoCsBadge({ estado }: { estado: string }) {
  const c = ESTADO_CS[estado] || { label: estado, className: 'bg-gray-100 text-gray-600' };
  return <span className={`badge ${c.className}`}>{c.label}</span>;
}

const CANAL_RECORDATORIO = { email: 'correo', sms: 'SMS' } as const;

function describirRecordatorios(r: RecordatoriosFirma): string {
  const canales = [...new Set(r.recordatorios.map(x => CANAL_RECORDATORIO[x.canal]))].join(' y ');
  const lapsos = r.recordatorios.map(x => lapsoLegible(x.horas));
  const lista = lapsos.length > 1 ? `${lapsos.slice(0, -1).join(', ')} y ${lapsos[lapsos.length - 1]}` : lapsos[0];
  return `a los ${lista}, por ${canales}`;
}

/**
 * Recordatorios que Click & Sign mandará con la configuración elegida. Van en
 * la configuración y no se pueden editar: cambiarlos crea otra en Click & Sign.
 */
function RecordatoriosConfigCaja({ configId, onCreada }: { configId: number; onCreada: (r: RecordatoriosConfig) => void }) {
  const [info, setInfo] = useState<RecordatoriosConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [dias, setDias] = useState('');
  const [creando, setCreando] = useState(false);

  useEffect(() => {
    let vigente = true;
    setInfo(null);
    setError(null);
    setEditando(false);
    api.recordatoriosConfigFirma(configId).then(r => {
      if (!vigente) return;
      setInfo(r);
      const actuales = r.recordatorios.filter(x => x.horas % 24 === 0).map(x => x.horas / 24);
      const propuestos = actuales.length ? actuales : [3, 7, 14].filter(d => !r.expiraHoras || d * 24 < r.expiraHoras);
      setDias([...new Set(propuestos)].join(', '));
    }).catch(e => { if (vigente) setError(e.message); });
    return () => { vigente = false; };
  }, [configId]);

  const crear = async () => {
    const lista = dias.split(/[\s,;]+/).filter(Boolean).map(Number);
    setCreando(true);
    try {
      const r = await api.crearConfigFirmaConRecordatorios(configId, lista);
      onCreada(r);
    } catch (e: any) {
      alert(e.message || 'No se pudo crear la configuración');
    } finally {
      setCreando(false);
    }
  };

  if (error) return <p className="text-xs text-amber-700 mt-2">No se pudieron leer los recordatorios de esta configuración ({error}).</p>;
  if (!info) return <p className="text-xs text-sereno-dark/40 mt-2 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Leyendo los recordatorios en Click &amp; Sign…</p>;

  const caduca = info.expiraHoras ? ` El enlace de firma caduca a los ${lapsoLegible(info.expiraHoras)}.` : '';
  return (
    <div className={classNames('mt-2 rounded-lg border p-3 text-sm', info.recordatorios.length ? 'border-gray-200 bg-gray-50' : 'border-amber-200 bg-amber-50 text-amber-800')}>
      <div className="flex items-start gap-2">
        {info.recordatorios.length ? <BellRing size={16} className="mt-0.5 shrink-0 text-sereno-dark/50" /> : <AlertTriangle size={16} className="mt-0.5 shrink-0" />}
        <p className="flex-1">
          {info.recordatorios.length
            ? <>Click &amp; Sign recordará la firma a quien aún no haya firmado {describirRecordatorios(info)}.{caduca}</>
            : <>Esta configuración no manda recordatorios: si no firman, Click &amp; Sign no se lo vuelve a pedir.{caduca}</>}
        </p>
        {!editando && (
          <button type="button" onClick={() => setEditando(true)} className="text-xs font-medium text-sereno-dark/60 hover:text-sereno-dark shrink-0">
            {info.recordatorios.length ? 'Cambiar' : 'Añadir recordatorios'}
          </button>
        )}
      </div>
      {editando && (
        <div className="mt-3 space-y-2">
          <label className="label">Días desde el envío</label>
          <div className="flex gap-2">
            <input className="input text-sm" placeholder="3, 7, 14" value={dias} onChange={e => setDias(e.target.value)} />
            <button type="button" onClick={crear} disabled={creando || !dias.trim()} className="btn-secondary text-sm shrink-0 flex items-center gap-1.5">
              {creando && <Loader2 size={14} className="animate-spin" />} Crear configuración
            </button>
            <button type="button" onClick={() => setEditando(false)} className="btn-ghost text-sm shrink-0">Cancelar</button>
          </div>
          <p className="text-xs text-sereno-dark/50">
            Click &amp; Sign no deja cambiar una configuración: se crea una copia de esta con estos recordatorios por correo (con el texto de los que ya tenía, o uno estándar si no tenía) y queda elegida.
            Marca «Recordar la configuración» para que sea la de este modelo en los próximos envíos.
          </p>
        </div>
      )}
    </div>
  );
}

function EnviarFirmaModal({ contrato, abierto, onClose, onEnviado }: { contrato: Contrato; abierto: boolean; onClose: () => void; onEnviado: (c: Contrato) => void }) {
  const [prep, setPrep] = useState<PreparacionFirma | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [configId, setConfigId] = useState<string>('');
  const [firmantes, setFirmantes] = useState<FirmanteEntrada[]>([]);
  const [serenoFirma, setSerenoFirma] = useState(true);
  const [recordar, setRecordar] = useState(true);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    let vigente = true;
    setPrep(null);
    setError(null);
    api.prepararFirmaContrato(contrato.id).then(p => {
      if (!vigente) return;
      setPrep(p);
      // Primero el ajuste guardado para este modelo; si no lo hay, la configuración
      // cuyo nombre casa con el modelo, prefiriendo las creadas para el CRM.
      const guardada = p.ajustes.configPorModelo?.[contrato.templateKey] ?? p.ajustes.configId ?? null;
      // La finalización usa una configuración propia si la hay; si no, la del contrato que cierra.
      const finalizado = contrato.valores?.modelo_finalizado === 'gestion' ? 'gestion' : 'explotacion';
      const patrones = contrato.templateKey === 'finalizacion'
        ? [/finaliz|rescis/i, finalizado === 'gestion' ? /gesti/i : /explotaci/i]
        : [contrato.templateKey === 'gestion' ? /gesti/i : /explotaci/i];
      const habilitadas = (p.configs || []).filter(c => !c.estado || c.estado === 'enabled');
      const delModelo = patrones.map(patron => habilitadas.filter(c => patron.test(c.nombre))).find(l => l.length > 0) || [];
      const propuesta = delModelo.find(c => /crm/i.test(c.nombre)) || delModelo.find(c => /nuevo/i.test(c.nombre)) || delModelo[0] || habilitadas[0];
      setConfigId(guardada ? String(guardada) : propuesta ? String(propuesta.configId) : '');
      setFirmantes(p.firmantes);
      setSerenoFirma(p.ajustes.serenoFirma !== false && p.firmantes.some(f => f.rol === 'sereno'));
    }).catch(e => { if (vigente) setError(e.message); });
    return () => { vigente = false; };
  }, [abierto, contrato.id]);

  const visibles = firmantes.filter(f => f.rol !== 'sereno' || serenoFirma);
  const actualizar = (rol: string, cambios: Partial<FirmanteEntrada>) => setFirmantes(fs => fs.map(f => (f.rol === rol ? { ...f, ...cambios } : f)));
  const configNombre = prep?.configs?.find(c => String(c.configId) === configId)?.nombre ?? null;
  // La copia con otros recordatorios entra en el desplegable y queda elegida.
  const configCreada = (r: RecordatoriosConfig) => {
    setPrep(p => (p?.configs && !p.configs.some(c => c.configId === r.configId)
      ? { ...p, configs: [...p.configs, { configId: r.configId, nombre: r.nombre || `Configuración ${r.configId}`, estado: 'enabled' }] }
      : p));
    setConfigId(String(r.configId));
  };
  // El año se fijó al generar: si esa temporada ya ha empezado, el propietario
  // firmaría como «primera temporada completa» una que va a medias.
  const temporada = Number(contrato.resuelto?.variables?.temporada_inicial) || null;
  const temporadaPasada = temporada !== null && temporadaEmpezada(temporada);
  const listo =!!configId && visibles.length > 0 && visibles.every(f => f.nombre.trim() && ((f.email || '').trim() || (f.telefono || '').trim()));

  const enviar = async () => {
    if (!listo) return;
    const quienes = visibles.map(f => `${f.nombre} ${f.apellidos}`.trim()).join(', ');
    if (!confirm(`Se enviará ${contrato.codigo} a firma en Click & Sign a: ${quienes}. Recibirán el aviso por correo o SMS. ¿Seguir?`)) return;
    setEnviando(true);
    try {
      const c = await api.enviarContratoAFirma(contrato.id, { configId: Number(configId), configNombre, firmantes: visibles, recordar });
      onEnviado(c);
      onClose();
    } catch (e: any) {
      alert(e.message || 'No se pudo enviar a firma');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal isOpen={abierto} onClose={onClose} title={`Enviar a firma · ${contrato.codigo}`} size="xl">
      {error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !prep ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-5">
          {temporadaPasada && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <p>
                Este contrato fija como Temporada Inicial la {temporada}, que empezó el {inicioTemporada(temporada!).split('-').reverse().join('-')}.
                El propietario firmaría como primera temporada completa una que ya va a medias. Duplica el contrato y genéralo de nuevo para que salga la {temporada! + 1}.
              </p>
            </div>
          )}
          <div>
            <label className="label">Configuración de firma en Click &amp; Sign</label>
            {prep.configs ? (
              <select className="input" value={configId} onChange={e => setConfigId(e.target.value)}>
                <option value="">Elige una configuración</option>
                {prep.configs.map(c => <option key={c.configId} value={String(c.configId)}>{c.nombre}{c.estado && c.estado !== 'enabled' ? ` (${c.estado})` : ''}</option>)}
              </select>
            ) : (
              <>
                <input className="input font-mono" placeholder="config_id" value={configId} onChange={e => setConfigId(e.target.value.replace(/\D/g, ''))} />
                <p className="text-xs text-amber-700 mt-1">No se pudo leer el listado de configuraciones ({prep.errorConfigs}). Escribe el número de la configuración.</p>
              </>
            )}
            <p className="text-xs text-sereno-dark/50 mt-1">La configuración decide cómo se avisa (correo, SMS), los recordatorios y cómo se firma. Los huecos del PDF los rellena el titular en la web de firma.</p>
            {Number(configId) > 0 && <RecordatoriosConfigCaja configId={Number(configId)} onCreada={configCreada} />}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label mb-0">Firmantes</label>
              {firmantes.some(f => f.rol === 'sereno') && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={serenoFirma} onChange={e => setSerenoFirma(e.target.checked)} /> Sereno también firma
                </label>
              )}
            </div>
            <div className="space-y-3">
              {visibles.map(f => (
                <div key={f.rol} className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-sereno-dark/50 mb-2">{ROL_FIRMANTE[f.rol]}</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                    <input className="input text-sm" placeholder="Nombre" value={f.nombre} onChange={e => actualizar(f.rol, { nombre: e.target.value })} />
                    <input className="input text-sm" placeholder="Apellidos" value={f.apellidos} onChange={e => actualizar(f.rol, { apellidos: e.target.value })} />
                    <input className="input text-sm" placeholder="Correo" type="email" value={f.email || ''} onChange={e => actualizar(f.rol, { email: e.target.value })} />
                    <input className="input text-sm" placeholder="Teléfono (+34…)" value={f.telefono || ''} onChange={e => actualizar(f.rol, { telefono: e.target.value })} />
                    <input className="input text-sm" placeholder="DNI (opcional)" value={f.dni || ''} onChange={e => actualizar(f.rol, { dni: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-sereno-dark/50 mt-2">Cada firmante necesita correo o teléfono. El segundo titular no suele tenerlos en la ficha: complétalos aquí.</p>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={recordar} onChange={e => setRecordar(e.target.checked)} />
            Recordar la configuración y el firmante de Sereno para los próximos contratos
          </label>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
            <button onClick={enviar} disabled={!listo || enviando} className="btn-primary text-sm flex items-center gap-1.5">
              {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar a firma
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const diaCorto = (iso: string) => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' });

/** Fechas en que Click & Sign recuerda la firma a quien no ha firmado, contando desde el envío. */
function CalendarioRecordatorios({ enviadoAt, recordatorios }: { enviadoAt: string; recordatorios: RecordatoriosFirma }) {
  const { recordatorios: lista, expiraAt } = calendarioRecordatorios(enviadoAt, recordatorios);
  const ahora = Date.now();
  if (lista.length === 0) {
    return <p className="text-xs text-amber-700">La configuración con que se envió no manda recordatorios{expiraAt ? `; el enlace caduca el ${diaCorto(expiraAt)}` : ''}.</p>;
  }
  return (
    <div className="text-xs text-sereno-dark/60">
      <p className="flex items-center gap-1.5 font-medium text-sereno-dark/70"><BellRing size={12} /> Recordatorios de Click &amp; Sign a quien no haya firmado</p>
      <p className="mt-1">
        {lista.map((r, i) => (
          <span key={i} className={new Date(r.at).getTime() < ahora ? 'text-sereno-dark/40 line-through' : ''}>
            {i > 0 && ' · '}{diaCorto(r.at)} ({CANAL_RECORDATORIO[r.canal]})
          </span>
        ))}
        {expiraAt && <> · caduca el {diaCorto(expiraAt)}</>}
      </p>
    </div>
  );
}

const TEXTO_RECORDATORIO = {
  enviado: { texto: 'enviado', clase: 'text-green-700' },
  programado: { texto: 'programado', clase: '' },
  no_hizo_falta: { texto: 'no hizo falta', clase: 'text-sereno-dark/40' },
  sin_confirmar: { texto: 'sin confirmar', clase: 'text-amber-700' },
  fallido: { texto: 'no entregado', clase: 'text-red-600' },
} as const;

/** Lo que Click & Sign le ha recordado a un firmante, según la última sincronización. */
function RecordatoriosFirmante({ firmante }: { firmante: FirmanteContrato }) {
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 text-xs text-sereno-dark/60 mt-0.5">
      <BellRing size={11} className="shrink-0" />
      {firmante.recordatorios!.map((r, i) => {
        const estado = estadoRecordatorio(r, firmante.estado);
        const { texto, clase } = TEXTO_RECORDATORIO[estado];
        return (
          <span key={i} className={clase} title={`Click & Sign: ${r.estadoCs}${r.estadoAt ? ` · ${formatDateTime(r.estadoAt)}` : ''}`}>
            {i > 0 && '· '}{diaCorto(r.programadoAt)}{r.canal === 'sms' ? ' (SMS)' : ''} {texto}
          </span>
        );
      })}
    </span>
  );
}

function FirmaCard({ contrato, onCambio }: { contrato: Contrato; onCambio: (c: Contrato) => void }) {
  const [abierto, setAbierto] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [estadoManual, setEstadoManual] = useState<EstadoContrato>(contrato.estado);
  const [signatureManual, setSignatureManual] = useState(contrato.signatureId || '');
  const firma = contrato.firma;

  const sincronizar = async () => {
    setOcupado(true);
    try {
      onCambio(await api.sincronizarFirmaContrato(contrato.id));
    } catch (e: any) {
      alert(e.message || 'No se pudo actualizar el estado');
    } finally {
      setOcupado(false);
    }
  };

  const guardarManual = async () => {
    setOcupado(true);
    try {
      onCambio(await api.cambiarEstadoContrato(contrato.id, estadoManual, signatureManual || undefined));
    } catch (e: any) {
      alert(e.message || 'No se pudo cambiar el estado');
    } finally {
      setOcupado(false);
    }
  };

  if (!firma) {
    return (
      <div className="card space-y-3">
        <p className="text-sm font-semibold">Firma</p>
        <p className="text-xs text-sereno-dark/50">Se envía a Click &amp; Sign con los campos rellenables y la posición de cada firma. El estado se actualiza solo cuando Click &amp; Sign avisa.</p>
        <button onClick={() => setAbierto(true)} className="btn-primary text-sm w-full flex items-center justify-center gap-1.5"><Send size={14} /> Enviar a firma</button>
        <details className="text-xs">
          <summary className="cursor-pointer text-sereno-dark/50">Apuntar el estado a mano (firma hecha fuera del CRM)</summary>
          <div className="space-y-2 mt-2">
            <select className="input text-sm" value={estadoManual} onChange={e => setEstadoManual(e.target.value as EstadoContrato)}>
              <option value="generado">Generado</option>
              <option value="enviado_firma">Enviado a firma</option>
              <option value="firmado">Firmado</option>
            </select>
            <input className="input text-sm font-mono" placeholder="signature_id de Click & Sign (opcional)" value={signatureManual} onChange={e => setSignatureManual(e.target.value)} />
            <button onClick={guardarManual} disabled={ocupado || (estadoManual === contrato.estado && signatureManual === (contrato.signatureId || ''))} className="btn-secondary text-sm w-full">Guardar estado</button>
          </div>
        </details>
        <EnviarFirmaModal contrato={contrato} abierto={abierto} onClose={() => setAbierto(false)} onEnviado={onCambio} />
      </div>
    );
  }

  const reenviable = ESTADOS_CS_REENVIABLES.includes(firma.estadoCs);
  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Firma en Click &amp; Sign</p>
        <EstadoCsBadge estado={firma.estadoCs} />
      </div>
      <p className="text-xs text-sereno-dark/50">
        Enviado el {formatDateTime(firma.enviadoAt)}{firma.configNombre ? ` · ${firma.configNombre}` : ''} · <span className="font-mono">{firma.signatureId}</span>
        {firma.sincronizadoAt && <> · comprobado {formatDateTime(firma.sincronizadoAt)}</>}
      </p>
      <ul className="text-sm space-y-1.5">
        {firma.firmantes.map(f => (
          <li key={f.rol} className="flex items-center justify-between gap-2">
            <span className="min-w-0">
              <span className="text-[11px] uppercase tracking-wide text-sereno-dark/40 mr-1">{ROL_FIRMANTE[f.rol]}</span>
              <span className="font-medium">{f.nombre} {f.apellidos}</span>
              <span className="block text-xs text-sereno-dark/50 truncate">{[f.email, f.telefono].filter(Boolean).join(' · ')}</span>
              {f.recordatorios && f.recordatorios.length > 0 && <RecordatoriosFirmante firmante={f} />}
            </span>
            {f.estado && <EstadoCsBadge estado={f.estado} />}
          </li>
        ))}
      </ul>
      {/* Hasta la primera sincronización no hay datos por firmante: se enseña lo que trae la configuración. */}
      {firma.recordatorios && !firma.firmantes.some(f => f.recordatorios) && firma.estadoCs !== 'signed' && !reenviable && (
        <CalendarioRecordatorios enviadoAt={firma.enviadoAt} recordatorios={firma.recordatorios} />
      )}
      <div className="grid gap-2 pt-2 border-t border-gray-100">
        {firma.pdfFirmado && (
          <a href={api.contratoPdfFirmadoUrl(contrato.id, { download: true })} className="btn-primary text-sm flex items-center justify-center gap-1.5"><Download size={14} /> Descargar PDF firmado</a>
        )}
        <button onClick={sincronizar} disabled={ocupado} className="btn-secondary text-sm flex items-center justify-center gap-1.5">
          {ocupado ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Actualizar estado
        </button>
        <Link to="/contracts/firmas" className="btn-ghost text-sm flex items-center justify-center gap-1.5"><ExternalLink size={14} /> Ver en Firmas</Link>
        {reenviable && (
          <button onClick={() => setAbierto(true)} className="btn-ghost text-sm flex items-center justify-center gap-1.5"><Send size={14} /> Volver a enviar</button>
        )}
      </div>
      <EnviarFirmaModal contrato={contrato} abierto={abierto} onClose={() => setAbierto(false)} onEnviado={onCambio} />
    </div>
  );
}

// ─── Contrato generado ──────────────────────────────────────────────────────

function ContratoGenerado({ contrato, onCambio }: { contrato: Contrato; onCambio: (c: Contrato) => void }) {
  const navigate = useNavigate();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const pdf = contrato.pdf;

  const duplicar = async () => {
    setOcupado('duplicar');
    try {
      const nuevo = await api.duplicarContrato(contrato.id);
      navigate(`/contracts/c/${nuevo.id}`);
    } catch (e: any) {
      alert(e.message || 'No se pudo duplicar');
    } finally {
      setOcupado(null);
    }
  };

  const anular = async () => {
    const motivo = prompt(`¿Anular el contrato ${contrato.codigo}? Motivo (opcional):`);
    if (motivo === null) return;
    setOcupado('anular');
    try {
      onCambio(await api.anularContrato(contrato.id, motivo || undefined));
    } catch (e: any) {
      alert(e.message || 'No se pudo anular');
    } finally {
      setOcupado(null);
    }
  };

  const guardarPreajuste = async () => {
    const nombre = prompt('Nombre del preajuste:');
    if (!nombre?.trim()) return;
    try {
      await api.guardarPreajusteDesdeContrato(contrato.id, nombre.trim());
      alert('Preajuste guardado.');
    } catch (e: any) {
      alert(e.message || 'No se pudo guardar');
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
      <div className="min-w-0">
        {pdf ? (
          <iframe title={`PDF ${contrato.codigo}`} src={api.contratoPdfUrl(contrato.id)} className="w-full h-[82vh] rounded-xl border border-gray-200 bg-white" />
        ) : (
          <div className="card text-sm text-sereno-dark/50">Este contrato no tiene PDF.</div>
        )}
      </div>
      <aside className="space-y-4">
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono text-sm font-semibold">{contrato.codigo}</p>
            <EstadoContratoBadge estado={contrato.estado} />
          </div>
          <div className="text-sm">
            <Link to={enlaceParte(contrato)} className="font-medium hover:underline flex items-center gap-1">{contrato.parte.nombre} <ExternalLink size={12} /></Link>
            <p className="text-xs text-sereno-dark/50">{NOMBRE_MODELO[contrato.templateKey]} · plantilla v{contrato.templateVersion}{contrato.preajusteNombre ? ` · ${contrato.preajusteNombre}` : ''}</p>
            {contrato.generadoAt && <p className="text-xs text-sereno-dark/50">Generado el {formatDateTime(contrato.generadoAt)}{pdf ? ` · ${pdf.paginas} páginas` : ''}</p>}
            {contrato.origenId && <p className="text-xs text-sereno-dark/50">Corrige a <Link className="underline" to={`/contracts/c/${contrato.origenId}`}>otro contrato</Link></p>}
            {contrato.notas && <p className="text-xs text-sereno-dark/60 mt-1 whitespace-pre-line">{contrato.notas}</p>}
          </div>
          <div className="grid gap-2 pt-2 border-t border-gray-100">
            {pdf && (
              <a href={api.contratoPdfUrl(contrato.id, { download: true })} className="btn-primary text-sm flex items-center justify-center gap-1.5"><Download size={14} /> Descargar PDF</a>
            )}
            <button onClick={duplicar} disabled={!!ocupado} className="btn-secondary text-sm flex items-center justify-center gap-1.5">
              {ocupado === 'duplicar' ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Duplicar como borrador
            </button>
            <button onClick={guardarPreajuste} className="btn-ghost text-sm flex items-center justify-center gap-1.5"><BookmarkPlus size={14} /> Guardar como preajuste</button>
            {contrato.estado !== 'anulado' && (
              <button onClick={anular} disabled={!!ocupado} className="text-xs text-red-500 hover:text-red-700 flex items-center justify-center gap-1 py-1"><Ban size={12} /> Anular</button>
            )}
          </div>
        </div>

        {contrato.estado !== 'anulado' && <FirmaCard contrato={contrato} onCambio={onCambio} />}

        {(contrato.estado !== 'anulado' || (contrato.consultas?.length ?? 0) > 0) && <ConsultaCard contrato={contrato} onCambio={onCambio} />}

        <div className="card">
          <p className="text-sm font-semibold mb-2">Historial</p>
          <HistorialLista entradas={contrato.historial} />
        </div>

        {contrato.resuelto && (
          <div className="card">
            <p className="text-sm font-semibold mb-2">Lo que difiere de la plantilla ({contrato.resuelto.diferencias.length})</p>
            {contrato.resuelto.diferencias.length === 0 ? <p className="text-xs text-sereno-dark/50">Nada: es la plantilla tal cual.</p> : (
              <ul className="space-y-1 text-xs">
                {contrato.resuelto.diferencias.map((d, i) => (
                  <li key={i}><span className="badge bg-amber-100 text-amber-800 mr-1">{TIPO_DIFERENCIA[d.tipo]}</span>{d.titulo || d.clausulaId}{d.detalle ? ` → ${d.detalle}` : ''}</li>
                ))}
              </ul>
            )}
            <p className="text-sm font-semibold mt-3 mb-1">Variables</p>
            <ul className="text-xs font-mono text-sereno-dark/70 space-y-0.5">
              {Object.entries(contrato.resuelto.variables).map(([k, v]) => <li key={k}>{k} = {v}</li>)}
            </ul>
          </div>
        )}

        {pdf && (
          <div className="card">
            <p className="text-sm font-semibold mb-1">Campos rellenables ({pdf.campos.length})</p>
            <p className="text-xs text-sereno-dark/50 mb-2">Son los que Click &amp; Sign dejará completar al firmante, por nombre.</p>
            {pdf.campos.length === 0 ? <p className="text-xs text-sereno-dark/50">Ninguno: todos los datos venían de la ficha.</p> : (
              <ul className="text-xs space-y-0.5 max-h-48 overflow-y-auto">
                {pdf.campos.map(c => <li key={c.nombre} className="flex justify-between gap-2"><span className="font-mono">{c.nombre}</span><span className="text-sereno-dark/50 truncate">{c.etiqueta} · pág. {c.pagina}</span></li>)}
              </ul>
            )}
            <p className="text-sm font-semibold mt-3 mb-1">Recuadros de firma</p>
            <ul className="text-xs space-y-0.5">
              {pdf.firmas.map(f => <li key={f.rol} className="flex justify-between gap-2"><span className="font-mono">{f.rol}</span><span className="text-sereno-dark/50">pág. {f.pagina} · x {f.xMm} mm · y {f.yMm} mm · {f.anchoMm}×{f.altoMm} mm</span></li>)}
            </ul>
          </div>
        )}
      </aside>
    </div>
  );
}

// ─── Página ─────────────────────────────────────────────────────────────────

export default function ContratoEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) { setContrato(null); return; }
    let vigente = true;
    setContrato(null);
    setError(null);
    api.getContrato(id).then(c => { if (vigente) setContrato(c); }).catch(e => { if (vigente) setError(e.message); });
    return () => { vigente = false; };
  }, [id]);

  if (!id) {
    const tipo = searchParams.get('tipo');
    return <NuevoContrato tipoInicial={tipo === 'owner' || tipo === 'lead' ? tipo : undefined} idInicial={searchParams.get('id') || undefined} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Link to="/contracts" className="p-2 rounded-lg hover:bg-sereno-blue/30 text-sereno-dark/50"><ArrowLeft size={18} /></Link>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {contrato ? contrato.codigo : 'Contrato'}
            {contrato && <EstadoContratoBadge estado={contrato.estado} />}
          </h1>
          <p className="text-sm text-sereno-dark/50">
            {contrato?.estado === 'borrador' ? 'Elige las condiciones, revisa el texto y genera el PDF.' : 'Documento congelado. Para cambiar algo, duplícalo como borrador.'}
          </p>
        </div>
      </div>
      {error ? (
        <div className="card border border-red-200 bg-red-50 text-red-700">{error}</div>
      ) : !contrato ? (
        <LoadingSpinner />
      ) : contrato.estado === 'borrador' ? (
        <EditorBorrador key={`${contrato.id}-${contrato.templateKey}-${contrato.templateVersion}`} contrato={contrato} onCambio={setContrato} />
      ) : (
        <ContratoGenerado contrato={contrato} onCambio={setContrato} />
      )}
    </div>
  );
}

export { CONDICIONES_VACIAS };
